/**
 * Controlled Google Places address input — the non-RFF counterpart of
 * `RffAddressEditingFieldGoogle`, for the panel-form sidebars.
 *
 * `value` is the LOS address object, `onChange` gets the next one (or null).
 * The search, the place → address mapping and the manual-entry helpers are
 * shared with the RFF field through `utils/geocode/googlePlaces`, so the two
 * can't disagree about what an address is.
 *
 * `allowManualEntry` exposes the override path: when Places returns nothing
 * useful — raw land, a new subdivision — the user types the parts themselves
 * and we geocode the ZIP to a centroid, stamping `manualEntry: true` and
 * `geocodeQuality: "zip"` so downstream surfaces know the coordinates are a
 * ZIP centroid and not a rooftop. Off by default: a borrower or contact
 * address should stay a strict Places match.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import EditLocationAltOutlined from "@mui/icons-material/EditLocationAltOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import LocationOffOutlined from "@mui/icons-material/LocationOffOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import debounce from "lodash/debounce";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { selectOptionsUsaStates } from "../../constants/selectOptions/selectOptionsUsaStates";
import { geocodeZipCentroid } from "../../utils/geocode/geocodeZipCentroid";
import {
  assembleFullAddress,
  fetchPlaceDetails,
  isValidZip,
  mapPlaceDetailsToAddress,
  parseSeedFromQuery,
  searchPlaces,
} from "../../utils/geocode/googlePlaces";
import { FORM, formSx, px } from "../../theme/formStyles";
import { slate } from "../../theme/colors";

const MANUAL_FIELDS = [
  { name: "streetNumber", label: "Street number", span: 2 },
  { name: "streetName", label: "Street name", span: 4 },
  { name: "city", label: "City", span: 3 },
  { name: "state", label: "State", span: 1, options: selectOptionsUsaStates },
  { name: "zip", label: "ZIP", span: 2 },
  { name: "county", label: "County", span: 3 },
  { name: "addressUnitNumber", label: "Unit / apt.", span: 3 },
];

const GoogleAddressInput = ({
  value,
  onChange,
  placeholder = "Start typing an address",
  disabled,
  allowManualEntry = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [manualError, setManualError] = useState(null);

  const runSearch = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setOptions([]);
      setShowNoResults(false);
      return;
    }
    setLoading(true);
    const results = await searchPlaces(query);
    setOptions(results);
    setShowNoResults(results.length === 0);
    setLoading(false);
  }, []);

  const debouncedSearch = useMemo(() => debounce(runSearch, 500), [runSearch]);

  useEffect(() => {
    if (inputValue) debouncedSearch(inputValue);
    else {
      setOptions([]);
      setShowNoResults(false);
    }
    return () => debouncedSearch.cancel();
  }, [inputValue, debouncedSearch]);

  const handlePick = async (_e, option) => {
    if (!option?.placeId) return;
    setLoading(true);
    try {
      const place = await fetchPlaceDetails(option.placeId);
      const address = mapPlaceDetailsToAddress(place, option.id);
      onChange(address);
      setInputValue(address.fullAddress);
      setIsEditing(false);
    } catch (err) {
      console.error("Google place details error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    onChange(null);
    setInputValue("");
    setOptions([]);
    setShowNoResults(false);
    setIsEditing(false);
    setManualMode(false);
    setManualError(null);
  };

  const enterManual = () => {
    setManualMode(true);
    setIsEditing(false);
    setOptions([]);
    setShowNoResults(false);
    setManualError(null);
    // Seed from whatever was typed so the parts aren't blank. Not geocoded
    // yet — `geocodeQuality` stays null until confirm.
    onChange({ ...parseSeedFromQuery(inputValue), manualEntry: true, geocodeQuality: null });
  };

  const confirmManual = async () => {
    const current = value || {};
    if (!isValidZip(current.zip)) {
      setManualError("A valid 5-digit ZIP is required to confirm.");
      return;
    }
    setGeocoding(true);
    setManualError(null);
    const centroid = await geocodeZipCentroid(current.zip);
    setGeocoding(false);
    if (!centroid) {
      setManualError("Could not look up that ZIP. Double-check it or try again.");
      return;
    }
    const fullAddress = assembleFullAddress(current);
    onChange({
      ...current,
      id: current.id || `manual-${Date.now()}`,
      fullAddress,
      country: current.country || "United States",
      countryCode: current.countryCode || "US",
      latitude: centroid.latitude,
      longitude: centroid.longitude,
      coordinateTuple: [centroid.latitude, centroid.longitude],
      manualEntry: true,
      geocodeQuality: "zip",
      zipCentroid: {
        latitude: centroid.latitude,
        longitude: centroid.longitude,
      },
      zipRadiusMiles: centroid.radiusMiles,
    });
    setManualMode(false);
    setInputValue(fullAddress);
  };

  /* ── manual entry: the parts, typed ── */
  if (manualMode) {
    const setPart = (name, v) => onChange({ ...(value || {}), [name]: v });
    return (
      // Its own panel: a tinted, hairline-bordered box, so the typed address
      // reads as one block apart from the fields around it.
      <Box
        sx={{
          p: px(FORM.s.s4),
          border: `1px solid ${FORM.color.divider}`,
          borderRadius: "10px",
          bgcolor: slate[50],
        }}
      >
        <Box sx={formSx.fieldGrid}>
          {MANUAL_FIELDS.map((f) => (
            <Box key={f.name} sx={formSx.fieldSpan(f.span)}>
              <Txt sx={{ ...formSx.label, display: "block" }}>{f.label}</Txt>
              <TextField
                select={!!f.options}
                size="small"
                fullWidth
                value={value?.[f.name] ?? ""}
                onChange={(e) => setPart(f.name, e.target.value)}
                // A select shows the short value ("NY"), the menu the full label.
                SelectProps={f.options ? { renderValue: (v) => v } : undefined}
                sx={{ ...formSx.input, "& .MuiInputBase-root": { bgcolor: "#FFFFFF" } }}
              >
                {/* Options are `{ label, value }` ("OH - Ohio" / "OH") or plain
                    strings; the menu shows the label, the field stores the value. */}
                {(f.options || []).map((o) => {
                  const opt = typeof o === "string" ? { label: o, value: o } : o;
                  return (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: FORM.type.body[0] }}>
                      {opt.label}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Box>
          ))}
        </Box>
        {manualError ? (
          <Txt sx={{ ...formSx.helper, color: FORM.color.required, display: "block" }}>
            {manualError}
          </Txt>
        ) : (
          <Txt sx={{ ...formSx.helper, display: "block" }}>
            The map pin goes at the center of the ZIP code and is marked as
            approximate.
          </Txt>
        )}
        <Flx ac g={1} sx={{ mt: px(FORM.s.s3) }}>
          <Button
            size="small"
            sx={formSx.button}
            disabled={geocoding}
            onClick={confirmManual}
          >
            {geocoding ? "Looking up ZIP…" : "Confirm address"}
          </Button>
          <Button
            size="small"
            variant="text"
            color="gray"
            sx={formSx.button}
            onClick={() => {
              setManualMode(false);
              setManualError(null);
            }}
          >
            Cancel
          </Button>
        </Flx>
      </Box>
    );
  }

  /* ── a chosen address: input-shaped, with edit + clear ── */
  if (value?.fullAddress && !isEditing) {
    return (
      <TextField
        size="small"
        fullWidth
        disabled={disabled}
        value={value.fullAddress}
        sx={formSx.input}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Edit address"
                onClick={() => {
                  setIsEditing(true);
                  setInputValue(value.fullAddress);
                  onChange(null);
                }}
                sx={{ color: FORM.color.ink3 }}
              >
                <EditOutlined sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Clear address"
                onClick={handleClear}
                sx={{ color: FORM.color.ink3 }}
              >
                <DeleteOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  }

  /* ── search ── */
  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.fullAddress || "")}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      loading={loading}
      inputValue={inputValue}
      onInputChange={(e, v, reason) => {
        if (reason === "input" || reason === "clear" || reason === "reset")
          setInputValue(v);
      }}
      onChange={handlePick}
      noOptionsText={null}
      PaperComponent={({ children, ...props }) => {
        // Autocomplete closes on blur, so mousedown has to beat it.
        const manualButton = (sx) => (
          <Button
            size="small"
            variant="outlined"
            color="gray"
            startIcon={<EditLocationAltOutlined />}
            sx={{ ...formSx.button, ...sx }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              enterManual();
            }}
          >
            Enter the address manually
          </Button>
        );
        if (showNoResults) {
          return (
            <Paper {...props} elevation={2}>
              <Box sx={{ p: px(FORM.s.s4), textAlign: "center" }}>
                <LocationOffOutlined
                  sx={{ fontSize: 32, color: FORM.color.ink4, mb: px(FORM.s.s2) }}
                />
                <Txt sx={{ ...formSx.helper, display: "block", mt: 0 }}>
                  No US addresses found for &quot;{inputValue}&quot;. Try adding
                  a city or state.
                </Txt>
                {allowManualEntry ? manualButton({ mt: px(FORM.s.s3) }) : null}
              </Box>
            </Paper>
          );
        }
        // Google can return matches that are all wrong (a unit it can't
        // resolve, a new build) — the override must not depend on getting
        // zero results, so it sits under the matches too.
        return (
          <Paper {...props} elevation={2}>
            {children}
            {allowManualEntry && options.length > 0 ? (
              <Box
                sx={{
                  px: px(FORM.s.s3),
                  py: px(FORM.s.s2),
                  borderTop: `1px solid ${FORM.color.divider}`,
                }}
              >
                <Txt sx={{ ...formSx.helper, display: "block", mt: 0, mb: px(FORM.s.s2) }}>
                  Not the right address?
                </Txt>
                {manualButton({})}
              </Box>
            ) : null}
          </Paper>
        );
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box sx={{ minWidth: 0 }}>
            <Txt sx={{ ...formSx.body, display: "block" }}>
              {option.mainText || option.fullAddress}
            </Txt>
            {option.secondaryText ? (
              <Txt sx={{ ...formSx.helper, display: "block", mt: 0 }}>
                {option.secondaryText}
              </Txt>
            ) : null}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder}
          sx={formSx.input}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ fontSize: 16, color: FORM.color.ink4 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

GoogleAddressInput.propTypes = {
  /** The LOS address object. */
  value: PropTypes.object,
  /** Called with the next address object, or null when cleared. */
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  /** Expose the type-it-yourself override (ZIP-centroid geocode). */
  allowManualEntry: PropTypes.bool,
};

export default GoogleAddressInput;
